import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

import { notificationService } from '../../services/notificationService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NotificationLogsPanel from './components/NotificationLogsPanel';
import NotificationStatsPanel from './components/NotificationStatsPanel';
import NotificationPreferencesPanel from './components/NotificationPreferencesPanel';
import TestNotificationPanel from './components/TestNotificationPanel';

const NotificationManagementCenter = () => {
  const { variants, transition } = usePageTransition();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/administrative-dashboard');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, prefsData] = await Promise.all([
        notificationService?.getNotificationStats(7),
        notificationService?.getNotificationPreferences(user?.id),
      ]);
      setStats(statsData);
      setPreferences(prefsData);
    } catch (error) {
      console.error('Error loading notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'logs', label: 'Historique des Notifications', icon: 'List' },
    { id: 'stats', label: 'Statistiques', icon: 'BarChart3' },
    { id: 'preferences', label: 'Préférences', icon: 'Settings' },
    { id: 'test', label: 'Test de Notification', icon: 'Send' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <motion.div 
        className="container mx-auto px-4 pt-24 md:pt-28 pb-8"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="Bell" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-foreground">
                Centre de Gestion des Notifications
              </h1>
              <p className="text-muted-foreground mt-1">
                Gérez vos notifications par email pour les alertes de stock, assignations d'équipe et rappels
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Mail" size={20} className="text-primary" />
                  <span className="text-sm text-muted-foreground">Total (7j)</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{stats?.total || 0}</p>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span className="text-sm text-muted-foreground">Envoyés</span>
                </div>
                <p className="text-2xl font-bold text-success">{stats?.sent || 0}</p>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="XCircle" size={20} className="text-error" />
                  <span className="text-sm text-muted-foreground">Échecs</span>
                </div>
                <p className="text-2xl font-bold text-error">{stats?.failed || 0}</p>
              </div>
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={20} className="text-warning" />
                  <span className="text-sm text-muted-foreground">En attente</span>
                </div>
                <p className="text-2xl font-bold text-warning">{stats?.pending || 0}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="border-b border-border">
            <div className="flex overflow-x-auto">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 md:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {activeTab === 'logs' && <NotificationLogsPanel />}
                {activeTab === 'stats' && <NotificationStatsPanel stats={stats} />}
                {activeTab === 'preferences' && (
                  <NotificationPreferencesPanel 
                    preferences={preferences} 
                    onUpdate={loadData}
                  />
                )}
                {activeTab === 'test' && <TestNotificationPanel />}
              </>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/administrative-dashboard')}
            className="flex items-center gap-2"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Retour au Tableau de Bord</span>
          </Button>
        </div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default NotificationManagementCenter;